import { convertToUrlSearchParams } from '../utils/helper.js';
import { api } from './api.js';
import { TAG_LIST_CONTACTS, TAG_LIST_MEETINGS, TAG_MEETING } from './tags.js';

export const calendarEndpoints = api.enhanceEndpoints({ addTagTypes: ['calendar'] }).injectEndpoints({
    endpoints: (builder) => ({
        addCalendarMeeting: builder.mutation({
            query: (data) => ({
                url: '/calendar/add',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_LIST_MEETINGS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getCalendarMeeting: builder.query({
            query: (id) => ({
                url: `/calendar/view/${id}`,
                method: 'GET'
            }),
            providesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getCalendarMeetingByToken: builder.query({
            query: ({ meetingToken, contactToken }) => ({
                url: `/meeting/${meetingToken}/${contactToken}`,
                method: 'GET'
            }),
            providesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        editCalendarMeeting: builder.mutation({
            query: ({ id, data }) => ({
                url: `/calendar/edit/${id}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_LIST_MEETINGS, TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getCalendarMeetings: builder.query({
            query: (params) => ({
                url: `/calendar?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_MEETINGS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        calendarMeetingAdvancedSearch: builder.query({
            query: (params) => ({
                url: `/calendar/search?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_MEETINGS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getCalendarLoad: builder.query({
            query: (params) => ({
                url: `/calendar/load?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_MEETINGS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getCalendarContacts: builder.query({
            query: (params) => ({
                url: `/misc/search-contacts?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_CONTACTS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getCalendarProjects: builder.query({
            query: (params) => ({
                url: `/misc/search-projects?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_CONTACTS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getCalendarOrganizations: builder.query({
            query: (params) => ({
                url: `/misc/search-organizations?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_CONTACTS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        addCalendarMeetingNotes: builder.mutation({
            query: ({ id, data, notesType }) => ({
                url: `/calendar/update-${notesType}/${id}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        addCalendarMeetingContext: builder.mutation({
            query: ({ id, data }) => ({
                url: `/calendar/update-context/${id}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        editCalendarMeetingAttendeeInfo: builder.mutation({
            query: ({ meetingId, id, data }) => ({
                url: `/calendar/update-participant/${meetingId}/info/${id}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        addCalendarMeetingLiveChat: builder.mutation({
            query: ({ id, data }) => ({
                url: `/calendar/live-chat/${id}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getCalendarMeetingLiveChat: builder.query({
            query: (id) => ({
                url: `/calendar/live-chat/${id}`,
                method: 'GET'
            }),
            providesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        addCalendarMeetingObjectivesStatus: builder.mutation({
            query: ({ id, data }) => ({
                url: `/calendar/update-objectives-status/${id}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        addCalendarMeetingActions: builder.mutation({
            query: ({ id, data }) => ({
                url: `/calendar/update-actions/${id}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getCalendarMeetingSummary: builder.query({
            query: (id) => ({
                url: `/calendar/summary/${id}`,
                method: 'GET'
            }),
            providesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        downloadCalendarMeetingDetails: builder.mutation({
            query: ({ id, name }) => ({
                url: `/calendar/meeting-pdf/${id}`,
                method: 'EXPORT',
                fileName: `${name}`,
                fileType: 'pdf'
            }),
            providesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        deleteCalendarMeeting: builder.mutation({
            query: ({ id, deleteSeries }) => ({
                url: `/calendar/delete/${id}?${convertToUrlSearchParams({ deleteSeries })}`,
                method: 'DELETE'
            }),
            invalidatesTags: [TAG_LIST_MEETINGS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        cancelCalendarMeeting: builder.mutation({
            query: (id) => ({
                url: `/calendar/cancel/${id}`,
                method: 'POST'
            }),
            invalidatesTags: [TAG_LIST_MEETINGS, TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        sendCalendarMeetingInvites: builder.mutation({
            query: ({ id, params }) => ({
                url: `/calendar/invite/${id}`,
                method: 'POST',
                body: params
            }),
            invalidatesTags: [TAG_MEETING],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        respondToCalendarMeetingInvitation: builder.mutation({
            query: ({ meetingToken, contactToken, params }) => ({
                url: `/meeting/${meetingToken}/${contactToken}`,
                method: 'POST',
                body: params
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        })
    })
});

export const {
    useAddCalendarMeetingMutation,
    useGetCalendarMeetingQuery,
    useGetCalendarMeetingByTokenQuery,
    useEditCalendarMeetingMutation,
    useLazyGetCalendarMeetingsQuery,
    useLazyCalendarMeetingAdvancedSearchQuery,
    useLazyGetCalendarLoadQuery,
    useLazyGetCalendarContactsQuery,
    useLazyGetCalendarProjectsQuery,
    useLazyGetCalendarOrganizationsQuery,
    useAddCalendarMeetingNotesMutation,
    useAddCalendarMeetingContextMutation,
    useEditCalendarMeetingAttendeeInfoMutation,
    useAddCalendarMeetingLiveChatMutation,
    useGetCalendarMeetingLiveChatQuery,
    useAddCalendarMeetingObjectivesStatusMutation,
    useAddCalendarMeetingActionsMutation,
    useGetCalendarMeetingSummaryQuery,
    useDownloadCalendarMeetingDetailsMutation,
    useDeleteCalendarMeetingMutation,
    useCancelCalendarMeetingMutation,
    useSendCalendarMeetingInvitesMutation,
    useRespondToCalendarMeetingInvitationMutation
} = calendarEndpoints;
